import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoaderCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

interface Props {
  open: boolean
  title: string
  initialName: string
  /** Resolves to an error text to show inline, or null when the save worked. */
  onSubmit: (name: string) => Promise<string | null>
  onClose: () => void
}

const FolderNameDialog: React.FC<Props> = ({ open, title, initialName, onSubmit, onClose }) => {
  const { t } = useTranslation()
  const [name, setName] = useState(initialName)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (open) {
      setName(initialName)
      setError(null)
    }
  }, [open, initialName])

  const trimmed = name.trim()
  const valid = trimmed.length >= 1 && trimmed.length <= 100

  const submit = async () => {
    if (!valid || saving) return
    setSaving(true)
    const failure = await onSubmit(trimmed)
    setSaving(false)
    if (failure) setError(failure)
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !saving && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="photo-folder-name">{t('Photos_folder_name')}</Label>
          <Input
            id="photo-folder-name"
            autoFocus
            maxLength={100}
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
          />
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={onClose} disabled={saving}>{t('Common_cancel')}</Button>
          <Button onClick={submit} disabled={!valid || saving}>
            {saving && <LoaderCircle className="h-4 w-4 animate-spin" />}
            {t('Common_save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default FolderNameDialog
